import { flatten } from "flat"
import { cloneDeep, isEqual } from "lodash-es"
import { deepObserve, IDisposer } from "mobx-utils"
import { sleep, validateUrl, validateV2 } from "polaris-enhance"

import APIReturnPage from "@/api/returnpage"
import { IReturnData, ReturnPageConfigType } from "@/api/returnpage/type"
import stores, { IStores } from "@/stores"
import toast from "@/utils/toast"

import { hideServerErr } from "./request"

export let disposer: IDisposer | null = null
export const formsDisposer: IDisposer | null = null
export let defaultPopupOptions: IReturnData
// export let defaultFormsOptions: Omit<FormsStoreProps, 'activeSaveBtn'> // 表单默认保存数据

class PopupsUtils {
  // 加密
  encrypt(id: number | string) {
    return window.btoa(String(id))
  }
  // 解密
  decode(value: string) {
    return window.atob(value)
  }
  /**
   * 注册监听器
   */
  registerListener() {
    disposer = deepObserve(stores.ReturnPageStore, (change) => {
      if (change.type === "update" && change.observableKind === "object") {
        const base = flatten<ReturnPageConfigType, ReturnPageConfigType>({
          style: defaultPopupOptions.style,
          text: defaultPopupOptions.text,
          is_remove_brand: defaultPopupOptions.is_remove_brand,
          store_contact_info: defaultPopupOptions.store_contact_info,
        })
        const now = flatten<ReturnPageConfigType, ReturnPageConfigType>({
          style: stores.ReturnPageStore.style,
          text: stores.ReturnPageStore.text,
          is_remove_brand: stores.ReturnPageStore.is_remove_brand,
          store_contact_info: stores.ReturnPageStore.store_contact_info,
        })
        const isChange = !isEqual(base, now)

        if (stores.CommonStore.popupSaveBtn.active !== isChange) {
          stores.CommonStore.setPopupSaveBtn("active", isChange)
        }
      }
    })
    return true
  }

  /**
   * 销毁监听器
   */
  destroyListener(focus: boolean = false) {
    const { CommonStore } = stores

    disposer && disposer()
    CommonStore.setPopupSaveBtn("loading", false)
    CommonStore.setPopupSaveBtn("active", false)
    focus && stores.ReturnPageStore.setDataLoadedTime(0)
    return true
  }

  /**
   * 设置初始值
   * @param options
   */
  setInitOptions(options: IReturnData) {
    defaultPopupOptions = cloneDeep(options)
    return true
  }

  /**
   * 获取初始值配置
   */
  getInitOptions(): IReturnData {
    return defaultPopupOptions
  }

  // /**
  //  * 判断规则输入框是否为空
  //  * @param array 规则
  //  */
  // checkURLInputIsEmpty(array: SpecificPage<any>[]): boolean {
  //   return array.some((item) => {
  //     if ([PageURLOperator.IsHomepage, PageURLOperator.IsNotHomepage].includes(item.operator)) return false
  //     return isEmpty(item.value)
  //   })
  // }

  /**
   * 保存配置信息
   */
  async saveLoginConfig({
    ReturnPageStore,
    CommonStore,
  }: Pick<IStores, "CommonStore" | "ReturnPageStore" | "UserInfoStore">) {
    CommonStore.setPopupSaveBtn("loading", true)
    const {
      text: text,
      style: style,
      is_remove_brand,
      activeLang,
      store_contact_info,
    } = ReturnPageStore

    if (style.login_pop_up.corner_radius === "") {
      style.login_pop_up.corner_radius = 0 // 如果没有值 则设置默认值
    }
    const error = validateUrl(text.return_policy_link.value)

    const isLinkNotPass = text.return_policy_link.type === "custom" && error

    // rules部分校验
    if (!store_contact_info && text.hide_merchant_contact_information === 0) {
      CommonStore.setPopupSaveBtn("loading", false)
      ReturnPageStore.setLeftSelected(0)
      await sleep(150)
      validateV2()
      return
    }

    // content部分校验
    if (
      [
        text.bottom_description,
        text.confirm_button_name,
        text.email_address,
        text.email_address_error_tips,
        text.order_email_address,
        text.order_number_error_tips,
        text.verification_code,
        text.verification_code_error_tips,
        text.order_order_number,
        text.return_policy_link_text_name,
      ].some((item) => item === "") ||
      isLinkNotPass
    ) {
      CommonStore.setPopupSaveBtn("loading", false)
      ReturnPageStore.setLeftSelected(1)
      await sleep(150)
      validateV2()
      return
    }

    if (text.return_policy_link.content === "<h2></h2>") {
      // 隐私内容为空
      CommonStore.setPopupSaveBtn("loading", false)
      // 如果没有显示 popup 要展示出来
      !ReturnPageStore.isPolicy && ReturnPageStore.setIsPolicy(true)
      return
    }
    // style部分校验
    if (
      (style.portal_top_content.type === "text" &&
        style.portal_top_content.value === "") ||
      style.primary_btn_text_color === "#" ||
      style.return_page_primary_color === "#"
    ) {
      CommonStore.setPopupSaveBtn("loading", false)
      ReturnPageStore.setLeftSelected(2)
      await sleep(300)
      validateV2()
      return
    }

    try {
      const {
        data: { data, code, message },
      } = await APIReturnPage.updateReturnConfig(ReturnPageStore.id, {
        text: text,
        style: style,
        is_remove_brand,
        code: activeLang,
        store_contact_info,
      })

      if (code !== 1000) {
        return toast(message, 5e3, true)
      }

      toast("Saved successfully")

      this.destroyListener()
      ReturnPageStore.setConfig(data)
      this.registerListener()
      CommonStore.setErrorState(false)
    } catch (e) {
      toast("Save failed", 5e3, true)
      CommonStore.setErrorState(true)
    } finally {
      CommonStore.setPopupSaveBtn("loading", false)
    }
  }
}

const PopupsUtilsExample = new PopupsUtils()

export default PopupsUtilsExample
